/**
 * LinkToResource - універсальний компонент для створення посилань на ресурси
 * 
 * Основні можливості:
 * - Автоматичне відкриття карточок через OpenResourceCardTask
 * - Підтримка різних режимів відображення (drawer/modal/page)
 * - Гнучка кастомізація UI (іконки, стилі, текст)
 * - Підтримка різних типів посилань (звичайне, кнопка, кастомне)
 * - Інтеграція з ResourceCorrespondenceConfig
 * - TypeScript типізація
 * 
 * @example
 * // Базове використання
 * <LinkToResource resourceKey="Company.Company" resourceId={123} />
 * 
 * @example
 * // З кастомним текстом та іконкою
 * <LinkToResource 
 *   resourceKey="Company.Company" 
 *   resourceId={123}
 *   text="Відкрити компанію"
 *   icon={<FaBuilding />}
 * />
 * 
 * @example
 * // Режим кнопки
 * <LinkToResource 
 *   resourceKey="Company.Company" 
 *   resourceId={123}
 *   variant="button"
 *   buttonType="primary"
 * />
 * 
 * @example
 * // Wrapper режим - повний контроль над UI
 * <LinkToResource resourceKey="Company.Company" resourceId={123} variant="wrapper">
 *   <div className="custom-content">Мій кастомний вміст</div>
 * </LinkToResource>
 * 
 * @example
 * // З callback'ами
 * <LinkToResource 
 *   resourceKey="Company.Company" 
 *   resourceId={123}
 *   onBeforeOpen={() => console.log('Opening...')}
 *   onAfterOpen={() => console.log('Opened!')}
 * />
 */

import React, { type ReactNode } from 'react'
import { Typography, Button, Tooltip } from 'antd'
import { FaExternalLinkAlt } from 'react-icons/fa'
import { OpenResourceCardTask, type TResourceDetailMode } from '@/Core/Tasks/Resources/OpenResourceCardTask'
import styles from './LinkToResource.module.css'
import { GetResourceTask } from '@/Core/Tasks/Resources/GetResourceTask'
import { ResourceCorrespondenceConfig } from '@/Ship/Configs/ResourceCorrespondenceConfig'

/**
 * Варіант відображення компонента
 */
export type LinkToResourceVariant = 'link' | 'button' | 'wrapper' | 'text'

/**
 * Пропси для LinkToResource
 */
export interface LinkToResourceProps {
    /** Ключ ресурсу (напр. "Company.Company") */
    resourceKey?: string

    /** ID ресурсу для відкриття */
    resourceId?: string | number

    /** Режим відкриття карточки */
    mode?: TResourceDetailMode

    /** Режим відображення карточки */
    displayMode?: 'page' | 'drawer' | 'modal'

    /** Текст посилання (якщо не вказано - використовується title ресурсу) */
    text?: string

    /** Іконка (за замовчуванням FaExternalLinkAlt) */
    icon?: ReactNode

    /** Показувати іконку */
    showIcon?: boolean

    /** Позиція іконки */
    iconPosition?: 'left' | 'right'

    /** Варіант відображення */
    variant?: LinkToResourceVariant

    /** Тип кнопки (для variant="button") */
    buttonType?: 'primary' | 'default' | 'dashed' | 'link' | 'text'

    /** Розмір кнопки */
    buttonSize?: 'small' | 'middle' | 'large'

    /** Tooltip */
    tooltip?: string

    /** Чи робити текст ellipsis */
    ellipsis?: boolean

    /** Кастомні стилі */
    style?: React.CSSProperties

    /** CSS класи */
    className?: string

    /** Children для wrapper або text варіанту */
    children?: ReactNode

    /** Контекст списку для передачі в карточку */
    listCrudContext?: any

    /** Callback перед відкриттям */
    onBeforeOpen?: () => boolean | void

    /** Callback після відкриття */
    onAfterOpen?: () => void

    /** Callback при помилці */
    onError?: (error: Error) => void

    /** Додаткові параметри для OpenResourceCardTask */
    openCardParams?: Partial<Parameters<typeof OpenResourceCardTask>[0]>

    /** 
     * Entity type для конвертації через ResourceCorrespondenceConfig
     * Якщо вказано - resourceKey буде визначено автоматично
     */
    entityType?: string

    /** Альтернативний ключ для entity (замість resourceId) */
    entityId?: string | number

    /** Чи заборонити клік */
    disabled?: boolean

    /** Кастомний обробник кліку (замість OpenResourceCardTask) */
    onClick?: (e: React.MouseEvent) => void
}

/**
 * Універсальний компонент для посилань на ресурси
 */
export const LinkToResource: React.FC<LinkToResourceProps> = ({
    resourceKey: propResourceKey,
    resourceId: propResourceId,
    mode = 'view',
    displayMode,
    text,
    icon,
    showIcon = true,
    iconPosition = 'right',
    variant = 'link',
    buttonType = 'link',
    buttonSize = 'middle',
    tooltip,
    ellipsis = false,
    style,
    className,
    children,
    listCrudContext,
    onBeforeOpen,
    onAfterOpen,
    onError,
    openCardParams,
    entityType,
    entityId,
    disabled = false,
    onClick,
}) => {
    // Визначаємо resourceKey та resourceId
    let finalResourceKey = propResourceKey
    let finalResourceId = propResourceId

    // Якщо вказано entityType - конвертуємо через ResourceCorrespondenceConfig
    if (entityType) {
        const correspondence = ResourceCorrespondenceConfig[entityType as keyof typeof ResourceCorrespondenceConfig]
        
        if (Array.isArray(correspondence)) {
            finalResourceKey = correspondence[0]
        } else if (correspondence && correspondence !== '?') {
            finalResourceKey = correspondence
        }
        
        if (entityId !== undefined) {
            finalResourceId = entityId
        }
    }

    // Отримуємо конфігурацію ресурсу
    const resourceConfig = finalResourceKey ? GetResourceTask(finalResourceKey) : null

    // Визначаємо текст для відображення
    const displayText = text || resourceConfig?.title || 'Ресурс'

    // Іконка за замовчуванням
    const displayIcon = icon !== undefined ? icon : <FaExternalLinkAlt style={{ fontSize: '12px' }} />

    /**
     * Обробник кліку
     */
    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()

        if (disabled) return

        // Кастомний обробник
        if (onClick) {
            onClick(e)
            return
        }

        try {
            // Callback перед відкриттям
            if (onBeforeOpen) {
                const result = onBeforeOpen()
                if (result === false) return
            }

            if (!resourceConfig) {
                throw new Error(`Resource not found: ${finalResourceKey}`)
            }

            // Відкриваємо карточку
            OpenResourceCardTask({
                resource: resourceConfig,
                primaryFieldValue: finalResourceId,
                mode,
                displayMode,
                listCrudContext,
                ...openCardParams,
            })

            // Callback після відкриття
            if (onAfterOpen) {
                onAfterOpen()
            }
        } catch (error) {
            console.error('[LinkToResource] Error opening resource:', error)
            if (onError) {
                onError(error as Error)
            }
        }
    }

    /**
     * Рендер вмісту з іконкою
     */
    const renderContent = () => {
        const textElement = ellipsis ? (
            <Typography.Text ellipsis>{displayText}</Typography.Text>
        ) : (
            <span>{displayText}</span>
        )

        if (!showIcon) {
            return textElement
        }

        if (iconPosition === 'left') {
            return (
                <>
                    {displayIcon}
                    {textElement}
                </>
            )
        }

        return (
            <>
                {textElement}
                {displayIcon}
            </>
        )
    }

    /**
     * Обгортка з tooltip
     */
    const withTooltip = (content: ReactNode) => {
        if (!tooltip) return content
        return <Tooltip title={tooltip}>{content}</Tooltip>
    }

    /**
     * Рендер в залежності від варіанту
     */
    const renderVariant = () => {
        switch (variant) {
            case 'button':
                return withTooltip(
                    <Button
                        type={buttonType}
                        size={buttonSize}
                        onClick={handleClick}
                        disabled={disabled}
                        style={style}
                        className={className}
                        icon={showIcon && iconPosition === 'left' ? displayIcon : undefined}
                    >
                        {iconPosition === 'left' ? displayText : renderContent()}
                    </Button>
                )

            case 'wrapper':
                return withTooltip(
                    <span
                        onClick={handleClick}
                        style={style}
                        className={`${styles.styledWrapper} ${disabled ? styles.disabled : ''} ${className || ''}`}
                    >
                        {children || renderContent()}
                    </span>
                )

            case 'text':
                return withTooltip(
                    <Typography.Text
                        onClick={handleClick}
                        style={{ cursor: disabled ? 'not-allowed' : 'pointer', ...style }}
                        className={className}
                        disabled={disabled}
                    >
                        {children || renderContent()}
                    </Typography.Text>
                )

            case 'link':
            default:
                return withTooltip(
                    <Typography.Link
                        onClick={handleClick}
                        style={style}
                        className={`${styles.styledLink} ${!disabled ? styles.hasHoverEffect : ''} ${className || ''}`}
                        disabled={disabled}
                    >
                        {renderContent()}
                    </Typography.Link>
                )
        }
    }

    return <>{renderVariant()}</>
}

/**
 * Готові варіанти компонента для зручності
 */

/** Посилання з іконкою справа */
export const LinkToResourceSimple: React.FC<Omit<LinkToResourceProps, 'variant'>> = (props) => (
    <LinkToResource {...props} variant="link" />
)

/** Кнопка для відкриття ресурсу */
export const LinkToResourceButton: React.FC<Omit<LinkToResourceProps, 'variant'>> = (props) => (
    <LinkToResource {...props} variant="button" buttonType="primary" />
)

/** Обгортка без власного UI */
export const LinkToResourceWrapper: React.FC<Omit<LinkToResourceProps, 'variant'>> = (props) => (
    <LinkToResource {...props} variant="wrapper" />
)

/**
 * Хук для програмного відкриття ресурсів
 */
export const useLinkToResource = () => {
    const openResource = React.useCallback((params: Omit<LinkToResourceProps, 'variant'>) => {
        const {
            resourceKey: propResourceKey,
            resourceId: propResourceId,
            mode = 'view',
            displayMode,
            listCrudContext,
            openCardParams,
            entityType,
            entityId,
        } = params

        let finalResourceKey = propResourceKey
        let finalResourceId = propResourceId

        // Конвертація entityType
        if (entityType) {
            const correspondence = ResourceCorrespondenceConfig[entityType as keyof typeof ResourceCorrespondenceConfig]
            
            if (Array.isArray(correspondence)) {
                finalResourceKey = correspondence[0]
            } else if (correspondence && correspondence !== '?') {
                finalResourceKey = correspondence
            }
            
            if (entityId !== undefined) {
                finalResourceId = entityId
            }
        }

        const resourceConfig = finalResourceKey ? GetResourceTask(finalResourceKey) : null

        if (!resourceConfig) {
            console.error(`[useLinkToResource] Resource not found: ${finalResourceKey}`)
            return
        }

        OpenResourceCardTask({
            resource: resourceConfig,
            primaryFieldValue: finalResourceId,
            mode,
            displayMode,
            listCrudContext,
            ...openCardParams,
        })
    }, [])

    return { openResource }
}

export default LinkToResource
